// Rejeu des écritures mises en file: dans l'ordre, une par une, avec la clé de
// rejeu qui garantit au serveur de ne les appliquer qu'une fois.
import { db } from '../db';
import { signalerEcriture } from '../fraicheur';
import { messageErreur } from '../erreurs';
import {
  correspondances,
  enregistrerCorrespondance,
  lireLigne,
  marquerConfirmee,
  supprimerEntite,
} from './store';
import { copie } from './paths';
import { renommerFichier } from './store';

const estReseauOuPanne = (erreur) => {
  const statut = erreur?.response?.status;
  if (!statut) return true;
  return statut >= 500 || [401, 408, 429].includes(statut);
};

/** Remplace les identifiants créés sur l'appareil par ceux du serveur. */
export function substituer(valeur, table) {
  if (valeur === null || valeur === undefined) return valeur;
  if (typeof valeur === 'string') {
    if (table[valeur]) return table[valeur];
    if (valeur.startsWith('/')) {
      return valeur
        .split('/')
        .map((segment) => table[decodeURIComponent(segment)] || segment)
        .join('/');
    }
    return valeur;
  }
  if (Array.isArray(valeur)) return valeur.map((element) => substituer(element, table));
  if (typeof valeur === 'object') {
    const resultat = {};
    Object.entries(valeur).forEach(([cle, element]) => {
      resultat[cle] = substituer(element, table);
    });
    return resultat;
  }
  return valeur;
}

const contientLocal = (valeur, ancien) => {
  if (typeof valeur === 'string') return valeur === ancien;
  if (Array.isArray(valeur)) return valeur.some((element) => contientLocal(element, ancien));
  if (valeur && typeof valeur === 'object') return Object.values(valeur).some((element) => contientLocal(element, ancien));
  return false;
};

/** Réécrit toutes les références locales (autres entités, pages mémorisées). */
export async function remplacerReferences(ancien, nouveau) {
  const table = { [ancien]: nouveau };
  await db.records.toCollection().modify((ligne) => {
    if (contientLocal(ligne.data, ancien)) ligne.data = substituer(ligne.data, table);
  });
  await db.httpCache.toCollection().modify((entree) => {
    if (entree.ids && entree.ids.includes(ancien)) {
      entree.ids = entree.ids.map((id) => (id === ancien ? nouveau : id));
    }
  });
}

async function renommerEntite(kind, ancien, nouveau, entiteServeur) {
  const ligne = await lireLigne(kind, ancien);
  await supprimerEntite(kind, ancien);
  const data = { ...(ligne?.data || {}), ...(entiteServeur || {}), id: nouveau };
  await db.records.put({ kind, id: String(nouveau), data, ts: Date.now(), pending: false });
}

const nombreDeRestantsSurEntite = async (kind, id, sauf) => {
  const lignes = await db.syncQueue.where('status').equals('pending').toArray();
  return lignes.filter(
    (autre) =>
      autre.id !== sauf &&
      autre.entity_type === 'http' &&
      (autre.data?.locaux || []).some((t) => t.kind === kind && String(t.id) === String(id)),
  ).length;
};

async function confirmerTouches(item, entiteServeur) {
  const table = await correspondances();
  for (const touche of item.data?.locaux || []) {
    const id = table[touche.id] || touche.id;
    if (touche.derive) continue;
    if (await nombreDeRestantsSurEntite(touche.kind, touche.id, item.id)) continue;
    if (touche.supprime) {
      await supprimerEntite(touche.kind, id);
    } else {
      await marquerConfirmee(touche.kind, id, touche.cree ? entiteServeur : null);
    }
  }
}

async function apprendreCorrespondances(item, reponse) {
  const corps = reponse?.data;
  const entite = corps?.data && typeof corps.data === 'object' && !Array.isArray(corps.data) ? corps.data : corps;
  const serveur = entite && typeof entite === 'object' && !Array.isArray(entite) ? entite : null;
  for (const touche of item.data?.locaux || []) {
    if (!touche.cree) continue;
    const idServeur = serveur?.id ?? serveur?._id ? String(serveur.id ?? serveur._id) : null;
    if (idServeur && idServeur !== String(touche.id)) {
      await enregistrerCorrespondance(touche.id, idServeur);
      // Le fichier déposé reste lisible sous son adresse définitive.
      if (item.data?.fichierLocal && serveur?.url) {
        const brute = String(serveur.url).replace(/^.*\/api\/v1/, '');
        await renommerFichier(item.data.fichierLocal, brute);
      }
      await renommerEntite(touche.kind, touche.id, idServeur, serveur);
      await remplacerReferences(String(touche.id), idServeur);
    }
  }
  return serveur;
}

/** Après un refus: l'appareil revient à la version du serveur. */
async function revenirALaVersionDuServeur(item, api) {
  const locaux = item.data?.locaux || [];
  const ids = new Set(locaux.map((t) => String(t.id)));
  for (const touche of locaux) {
    if (await nombreDeRestantsSurEntite(touche.kind, touche.id, item.id)) {
      // D'autres modifications attendent encore pour cette entité: elles
      // seront refusées à leur tour si elles dépendent de celle-ci.
    }
    if (touche.cree || touche.derive) {
      await supprimerEntite(touche.kind, touche.id);
      continue;
    }
    await marquerConfirmee(touche.kind, touche.id, null);
    try {
      // Le passage par la passerelle remet la version du serveur dans le modèle local.
      await api.get(`${touche.kind}/${encodeURIComponent(touche.id)}`, { skipToast: true, reseauDabord: true });
    } catch (erreur) {
      if (erreur?.response?.status === 404) await supprimerEntite(touche.kind, touche.id);
    }
  }
  // Les envois qui dépendent d'une entité jamais créée ne peuvent plus aboutir.
  const enAttente = await db.syncQueue.where('status').equals('pending').toArray();
  for (const autre of enAttente) {
    if (autre.id === item.id || autre.entity_type !== 'http') continue;
    const cible = JSON.stringify([autre.data?.url, autre.data?.params, autre.data?.corps]);
    const dependant = locaux.some((t) => t.cree && cible.includes(String(t.id)));
    if (dependant && [...ids].length) {
      await db.syncQueue.update(autre.id, {
        status: 'failed',
        last_error: 'Dépend d\'un enregistrement refusé',
        attempts: (autre.attempts || 0) + 1,
      });
      for (const t of autre.data?.locaux || []) {
        if (t.cree) await supprimerEntite(t.kind, t.id);
        else await marquerConfirmee(t.kind, t.id, null);
      }
    }
  }
}

/**
 * Rejoue un envoi. Rend 'ok' ou 'refus'; lève l'erreur d'origine quand le
 * réseau ou le serveur est indisponible (l'envoi reste en file, intact).
 */
export async function rejouerRequete(item, { api, syncQueue }) {
  const donnees = item.data || {};
  const table = await correspondances();
  let corps = donnees.corps === null ? undefined : substituer(donnees.corps, table);
  const entetes = { ...(donnees.entetes || {}), 'X-Replay-Key': donnees.cleRejeu };
  let delai;
  if (donnees.fichier) {
    // Document ou photo gardé sur l'appareil: renvoyé tel quel.
    corps = new FormData();
    corps.append('file', donnees.fichier, donnees.nomFichier || 'document');
    Object.entries(substituer(donnees.champs || {}, table)).forEach(([nom, valeur]) => corps.append(nom, valeur));
    entetes['Content-Type'] = 'multipart/form-data';
    delai = 60000;
  }
  const config = {
    method: donnees.methode,
    url: substituer(donnees.url, table),
    params: substituer(donnees.params, table),
    data: corps,
    headers: entetes,
    skipGateway: true,
    ...(delai ? { timeout: delai } : {}),
  };
  let reponse;
  try {
    reponse = await api.request(config);
  } catch (erreur) {
    if (estReseauOuPanne(erreur)) throw erreur;
    await syncQueue.markAttemptFailed([item.id], {
      reason: messageErreur(erreur, 'Refusée'),
      definitive: true,
    });
    await revenirALaVersionDuServeur(item, api);
    signalerEcriture({ method: donnees.methode, url: donnees.url });
    return 'refus';
  }
  const serveur = await apprendreCorrespondances(item, reponse);
  await confirmerTouches(item, serveur ? copie(serveur) : null);
  await syncQueue.markSynced([item.id]);
  await syncQueue.clearSynced();
  return 'ok';
}

/** Libère les entités d'un envoi abandonné par l'opérateur (retour à la version du serveur). */
export async function liberer(item, api) {
  if (item?.entity_type !== 'http') return;
  await revenirALaVersionDuServeur({ ...item, id: item.id }, api);
}
